import { useState, useEffect } from "react";
import { Activity, Shield, AlertTriangle, CheckCircle, Clock, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { BentoGrid, BentoGridItem } from "@/components/ui/bento-grid";
import { useToast } from "@/components/ui/use-toast";
import { RequestCounter } from "@/components/request-counter";
import { RateLimitWarnings } from "@/components/rate-limit-warnings";

interface RateLimitUsage {
  count: number;
  resetTime: string;
}

interface RateLimitStats {
  second: RateLimitUsage;
  minute: RateLimitUsage;
  hour: RateLimitUsage;
  day: RateLimitUsage;
}

interface RateLimitConfig {
  perSecond?: number;
  perMinute?: number;
  perHour?: number;
  perDay?: number;
}

interface RateLimitData {
  enabled: boolean;
  usage?: {
    ip?: {
      dns: RateLimitStats;
      api: RateLimitStats;
    };
    organization?: {
      dns: RateLimitStats;
      api: RateLimitStats;
    };
  };
  limits?: {
    dns: RateLimitConfig;
    api: RateLimitConfig;
    organization: RateLimitConfig;
  };
}

interface RateLimitStatusProps {
  dnsServerUrl?: string;
  refreshInterval?: number;
}

export function RateLimitStatus({ 
  dnsServerUrl = import.meta.env.VITE_DNS_SERVER_URL ?? "http://localhost:3001",
  refreshInterval = 30000 
}: RateLimitStatusProps) {
  const [rateLimitData, setRateLimitData] = useState<RateLimitData>({ enabled: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchRateLimitData = async () => {
    try {
      const response = await fetch(`${dnsServerUrl}/rate-limits`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setRateLimitData(data);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Failed to fetch rate limit data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRateLimitData();
    const interval = setInterval(fetchRateLimitData, refreshInterval);
    return () => clearInterval(interval);
  }, [dnsServerUrl, refreshInterval]);

  const formatTimeRemaining = (resetTime: string) => {
    const now = new Date().getTime();
    const reset = new Date(resetTime).getTime();
    const remaining = Math.max(0, reset - now);
    
    if (remaining < 60000) return `${Math.ceil(remaining / 1000)}s`;
    if (remaining < 3600000) return `${Math.ceil(remaining / 60000)}m`;
    if (remaining < 86400000) return `${Math.ceil(remaining / 3600000)}h`;
    return `${Math.ceil(remaining / 86400000)}d`;
  };

  const calculateUsagePercentage = (count: number, limit: number) => {
    return Math.min(100, (count / limit) * 100);
  };

  const getUsageStatus = (percentage: number) => {
    if (percentage >= 90) return { status: 'critical', color: 'bg-red-500', icon: AlertTriangle };
    if (percentage >= 75) return { status: 'warning', color: 'bg-yellow-500', icon: Clock };
    return { status: 'normal', color: 'bg-green-500', icon: CheckCircle };
  };

  if (!rateLimitData.enabled) {
    return (
      <Card className="bg-background/80 backdrop-blur-xl border border-primary/20">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-foreground">Rate Limiting</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-muted-foreground mb-2">Rate limiting is currently disabled</div>
            <Badge variant="outline" className="text-muted-foreground">
              Disabled
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="bg-background/80 backdrop-blur-xl border border-primary/20">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Activity className="h-5 w-5 text-primary animate-pulse" />
            <CardTitle className="text-foreground">Rate Limiting Status</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Loading rate limit data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-background/80 backdrop-blur-xl border border-primary/20">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <CardTitle className="text-foreground">Rate Limiting Status</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-red-500 mb-4">Failed to load rate limit data</div>
            <div className="text-sm text-muted-foreground mb-4">{error}</div>
            <Button onClick={fetchRateLimitData} variant="outline">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { usage, limits } = rateLimitData;

  if (!usage || !limits) {
    return (
      <Card className="bg-background/80 backdrop-blur-xl border border-primary/20">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle className="text-foreground">Rate Limiting Status</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-muted-foreground">No usage data available</div>
            <Badge variant="outline" className="text-green-600 border-green-500/30 mt-2">
              Active
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  const renderUsageCard = (
    title: string, 
    stats: RateLimitStats, 
    config: RateLimitConfig,
    type: 'dns' | 'api',
    scope: 'ip' | 'organization'
  ) => {
    const windows = [
      { key: 'minute', label: 'Per Minute', limit: config.perMinute },
      { key: 'hour', label: 'Per Hour', limit: config.perHour },
      { key: 'day', label: 'Per Day', limit: config.perDay }
    ].filter(w => w.limit);

    const mostCritical = windows.reduce((prev, curr) => {
      const prevPercentage = calculateUsagePercentage(stats[prev.key as keyof RateLimitStats]?.count || 0, prev.limit || 1);
      const currPercentage = calculateUsagePercentage(stats[curr.key as keyof RateLimitStats]?.count || 0, curr.limit || 1);
      return prevPercentage > currPercentage ? prev : curr;
    }, windows[0]);

    const criticalPercentage = mostCritical ? 
      calculateUsagePercentage(stats[mostCritical.key as keyof RateLimitStats]?.count || 0, mostCritical.limit || 1) : 0;
    
    const statusInfo = getUsageStatus(criticalPercentage);
    const StatusIcon = statusInfo.icon;

    return (
      <BentoGridItem
        key={`${scope}-${type}`}
        className="lg:col-span-1"
        icon={<StatusIcon className="h-4 w-4" />}
        glowColor={statusInfo.status === 'critical' ? '#EF4444' : 
                   statusInfo.status === 'warning' ? '#F59E0B' : '#10B981'}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-foreground">{title}</h4>
            <Badge variant="outline" className={`text-xs ${
              statusInfo.status === 'critical' ? 'text-red-500 border-red-500/30' :
              statusInfo.status === 'warning' ? 'text-yellow-500 border-yellow-500/30' :
              'text-green-500 border-green-500/30'
            }`}>
              {Math.round(criticalPercentage)}% used
            </Badge>
          </div>

          <div className="space-y-3">
            {windows.map((window) => {
              const usage = stats[window.key as keyof RateLimitStats];
              const percentage = calculateUsagePercentage(usage?.count || 0, window.limit || 1);
              
              return (
                <div key={window.key} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{window.label}</span>
                    <span className="text-foreground font-medium">
                      {usage?.count || 0} / {window.limit}
                    </span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Resets in {formatTimeRemaining(usage?.resetTime || '')}</span>
                    <span>{Math.round(percentage)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </BentoGridItem>
    );
  };

  return (
    <div className="space-y-6">
      {/* Rate Limit Warnings */}
      <RateLimitWarnings 
        dnsServerUrl={dnsServerUrl}
        refreshInterval={refreshInterval}
      />
      
      {/* Request Counter Component */}
      <RequestCounter 
        showDNS={true}
        showAPI={true}
        enableWarnings={true}
        refreshInterval={1000}
      />
      
      <Card className="bg-background/80 backdrop-blur-xl border border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <CardTitle className="text-foreground">Server Rate Limiting Status</CardTitle>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant="outline" className="text-green-600 border-green-500/30">
                Active
              </Badge>
              <Button onClick={fetchRateLimitData} variant="ghost" size="sm">
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground mb-6">
            Real-time monitoring of DNS and API request usage against configured limits.
          </div>

          {usage.ip && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Activity className="h-4 w-4" />
                <span>Your IP Usage</span>
              </h3>
              <BentoGrid className="grid-cols-1 lg:grid-cols-2 gap-4">
                {renderUsageCard("DNS Queries", usage.ip.dns, limits.dns, 'dns', 'ip')}
                {renderUsageCard("API Requests", usage.ip.api, limits.api, 'api', 'ip')}
              </BentoGrid>
            </div>
          )}

          {usage.organization && (
            <>
              <Separator className="my-6" />
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                  <Shield className="h-4 w-4" />
                  <span>Organization Usage</span>
                </h3>
                <BentoGrid className="grid-cols-1 lg:grid-cols-2 gap-4">
                  {renderUsageCard("DNS Queries", usage.organization.dns, limits.organization, 'dns', 'organization')}
                  {renderUsageCard("API Requests", usage.organization.api, limits.organization, 'api', 'organization')}
                </BentoGrid>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}